import { Analytics } from '@easy-expense/analytics-client';
import { currentUser, sendTrialExpiryEmail, signUp } from '@easy-expense/auth-client';
import {
  useCurrentWorkspace,
  useUserSettings,
  useWorkspaceSettings,
} from '@easy-expense/data-firestore-client';
import { BUSINESS_OWNER_TYPE } from '@easy-expense/data-schema-v2';
import { WebEnv } from '@easy-expense/env-mobile';
import { getTranslation } from '@easy-expense/intl-client';
import { DoNotFixMeIAmAny } from '@easy-expense/utils-typescript';
import { Environments, initializePaddle, Paddle as PaddleInstance } from '@paddle/paddle-js';
import React from 'react';
import ReactPixel from 'react-facebook-pixel';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Country, FlagType, getAllCountries } from 'react-native-country-picker-modal';
import { useNavigate } from 'react-router-dom';

import { CountrySelector, DEFAULT_COUNTRY } from './CountrySelector';
import { DeductionFinder } from './DeductionFinder';
import {
  BusinessRegistrationSurvey,
  IndustrySurvey,
  TeamSurvey,
  UsageSurvey,
} from './OnboardingSurveys';
import { OnboardingExpense, ReceiptScanner } from './ReceiptScanner';
import { Welcome } from './Welcome';
import { PaddleCheckout } from '../../components/OnboardingFlow/Checkout/PaddleCheckout';
import {
  OnboardingFlow,
  OnboardingStep,
  OnboardingCurrentStep,
} from '../../components/OnboardingFlow/OnboardingFlow';
import { Signup } from '../../components/OnboardingFlow/Signup/Signup';
import { EmailSchema } from '../../components/SignUp.form';
import { auth } from '../../firebase/app';
import { useUserLocation } from '../../hooks/useUserLocation';
import {
  paddleBusinessItemsProd,
  paddleBusinessItemsSandbox,
  paddleClientTokenProd,
  paddleClientTokenSandbox,
} from '../../lib/Paddle';
import { createWorkspace } from '../../lib/createWorkspace';

export type OnboardingState = OnboardingCurrentStep & {
  teamSurveyOptions: string[];
  usageOptions: string[];
  industries: string[];
  typeOptions: string[];
  businessRegistration: string[];
  trialStarted: boolean;
  scannedReceipt?: OnboardingExpense;
  email?: string;
  emailError?: string;
  password?: string;
  workspaceID?: string;
  countryCode?: string;
  provinceName?: string;
};

const emptyOnboardingState: OnboardingState = {
  currentStepIndex: 0,
  teamSurveyOptions: [],
  usageOptions: [],
  typeOptions: [],
  industries: [],
  businessRegistration: [],
  trialStarted: false,
};

//Artificial amount of time to wait for the categories to load
export const CATEGORY_LOADING_WAIT = 7000;

export const ReceiptScanningOnboarding: React.FC = () => {
  const [onboardingState, setOnboardingState] =
    React.useState<OnboardingState>(emptyOnboardingState);
  const { updateWorkspaceSetting } = useWorkspaceSettings();
  const currentWorkspace = useCurrentWorkspace();
  const { updateUserSetting } = useUserSettings();
  const navigate = useNavigate();
  const [country, setCountry] = React.useState<Country>(DEFAULT_COUNTRY);
  const [countryList, setCountryList] = React.useState<Country[]>([]);
  const location = useUserLocation();

  const [user] = useAuthState(auth);

  const userCompletedSignUp = React.useMemo(() => {
    return !user?.isAnonymous;
  }, [user?.isAnonymous]);

  const paddleRef = React.useRef<PaddleInstance | null>(null);
  const [paddleItemsPricings, setPaddleItemsPricings] = React.useState(paddleBusinessItemsProd);

  React.useEffect(() => {
    let paddleEnv: Environments = 'production';
    let paddleClientToken = paddleClientTokenProd;
    if (WebEnv.buildType !== 'production') {
      console.log('using sandbox paddle');
      paddleEnv = 'sandbox';
      paddleClientToken = paddleClientTokenSandbox;
      setPaddleItemsPricings(paddleBusinessItemsSandbox);
    }

    initializePaddle({
      environment: paddleEnv,
      token: paddleClientToken,
      async eventCallback(data) {
        if (data.name === 'checkout.completed') {
          setOnboardingState((prevState) => {
            return { ...prevState, trialStarted: true };
          });
          Analytics.track('trial_started');
          ReactPixel.trackCustom('Trial Started');
          sendTrialExpiryEmail()();
          closePaddleCheckout();
          navigate('/onboarding/get-started');
        }
      },
    })
      .then((paddleInstance: PaddleInstance | undefined) => {
        if (paddleInstance) {
          paddleRef.current = paddleInstance;
        }
      })
      .catch((error) => {
        console.error('Error initializing Paddle: ', error);
      });
  }, []);

  React.useEffect(() => {
    getAllCountries(FlagType.EMOJI).then((countries) => {
      setCountryList(countries);
    });
  }, []);

  React.useEffect(() => {
    const locatedCountry = countryList.find((country) => country.cca2 === location?.cca2);
    if (locatedCountry) {
      const { callingCode, name, cca2, region, flag, subregion, currency } = locatedCountry;
      if (cca2 === 'CA' && location?.state) {
        setOnboardingState({ ...onboardingState, provinceName: location.state, countryCode: cca2 });
      } else {
        setOnboardingState({ ...onboardingState, countryCode: cca2 });
      }
      setCountry({
        callingCode,
        cca2,
        name,
        region,
        flag,
        subregion,
        currency,
      });
    }
  }, [location]);

  React.useEffect(() => {
    if (!user) {
      return;
    }
    if (!currentWorkspace) {
      createWorkspaceAsync();
    }
  }, [user]);

  function closePaddleCheckout() {
    if (!paddleRef.current) {
      alert('There was an error closing Paddle checkout');
      return;
    }
    paddleRef.current.Checkout.close();
  }

  function startPaddleCheckout() {
    if (!paddleRef.current) {
      alert('There was an error starting Paddle payment');
      return;
    }

    const itemPrice =
      onboardingState.countryCode === 'CA'
        ? 'MONTHLY_7DAYTRIAL_FIVE_DOLLAR_CAD'
        : 'MONTHLY_7DAYTRIAL_FIVE_DOLLAR';

    paddleRef.current.Checkout.open({
      items: [{ priceId: paddleItemsPricings[itemPrice] }],
      settings: { showAddDiscounts: false },
      customer: {
        email: onboardingState.email ?? '',
      },
      customData: {
        organizationID: currentWorkspace?.org,
        workspaceID: currentWorkspace?.key,
        userID: currentUser()?.uid ?? '',
      },
    });
  }

  const createWorkspaceAsync = async () => {
    if (currentWorkspace?.key) {
      return;
    }
    await createWorkspace('business', 'Default');

    updateWorkspaceSetting({ onboardingType: 'team' });
  };

  const steps: OnboardingStep[] = [
    {
      continueText: 'Get Started',
      stepKey: 'welcome',
      onContinue: () => {
        ReactPixel.trackCustom('Get Started');
        return Promise.resolve(true);
      },
      canContinue: true,
      component: () => <Welcome />,
    },

    {
      continueText: 'Continue',
      stepKey: 'usage_type',
      canContinue: onboardingState.usageOptions.length > 0,
      onContinue: () => {
        Analytics.track('onboarding_question_answered', {
          question: 'type_question',
          values: onboardingState.usageOptions,
        });
        return Promise.resolve(true);
      },
      component: () => (
        <UsageSurvey
          selectedOptions={onboardingState.usageOptions}
          setUsageOptions={(usageOptions: string[]) => {
            setOnboardingState({ ...onboardingState, usageOptions });
          }}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'team',
      canContinue: onboardingState.teamSurveyOptions.length > 0,
      onContinue: () => {
        Analytics.track('onboarding_question_answered', {
          question: 'team_or_personal',
          value: onboardingState.teamSurveyOptions[0] ?? '',
        });

        return Promise.resolve(true);
      },
      component: () => (
        <TeamSurvey
          selectedOptions={onboardingState.teamSurveyOptions}
          setTeamSurveyOptions={(teamSurveyOptions: string[]) => {
            setOnboardingState({ ...onboardingState, teamSurveyOptions });
          }}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'country',
      canContinue:
        onboardingState.countryCode !== undefined &&
        (onboardingState.countryCode !== 'CA' || onboardingState.provinceName !== undefined),
      component: () => (
        <CountrySelector
          province={onboardingState.provinceName}
          country={country}
          setCountry={(country: Country) => {
            setOnboardingState({ ...onboardingState, countryCode: country.cca2 });
            setCountry(country);
          }}
          setProvince={(provinceName: string) => {
            setOnboardingState({ ...onboardingState, provinceName });
          }}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'business_registration',
      canContinue: onboardingState.businessRegistration.length > 0,
      onContinue: async () => {
        Analytics.track('onboarding_question_answered', {
          question: 'business_registration',
          value: onboardingState.businessRegistration[0] ?? '',
        });
        updateWorkspaceSetting({
          businessRegistration: BUSINESS_OWNER_TYPE.parse(onboardingState.businessRegistration[0]),
        });
        return Promise.resolve(true);
      },
      component: () => (
        <BusinessRegistrationSurvey
          countryCode={country.cca2}
          businessRegistration={onboardingState.businessRegistration}
          setBusinessRegistration={(businessRegistration: string[]) => {
            setOnboardingState({ ...onboardingState, businessRegistration });
          }}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'industry',
      loadingText: getTranslation('Finding your Deductions...'),
      loadingComponent: () => <DeductionFinder />,
      canContinue: onboardingState.industries.length > 0,
      onContinue: async () => {
        Analytics.identify(user?.uid ?? null, {
          industry: onboardingState.industries,
        });
        if (onboardingState.usageOptions.includes('Tax Deductions')) {
          return await new Promise((resolve) => {
            setTimeout(() => {
              resolve(true);
            }, CATEGORY_LOADING_WAIT);
          });
        } else {
          return Promise.resolve(true);
        }
      },
      component: () => (
        <IndustrySurvey
          selectedOptions={onboardingState.industries}
          onSelect={(industries: string[]) => {
            setOnboardingState({ ...onboardingState, industries });
          }}
        />
      ),
    },
    {
      continueText: 'Continue',
      stepKey: 'receipt_scan',
      canContinue: onboardingState.scannedReceipt !== undefined,
      onContinue: () => {
        ReactPixel.trackCustom('Scanned Receipt');
        return Promise.resolve(true);
      },
      component: () => (
        <ReceiptScanner
          countryCode={onboardingState.countryCode}
          scannedReceipt={onboardingState.scannedReceipt}
          setScannedReceipt={(scannedReceipt: OnboardingExpense) =>
            setOnboardingState({ ...onboardingState, scannedReceipt })
          }
        />
      ),
      fullWidth: true,
    },
    {
      continueText: userCompletedSignUp ? 'Continue' : 'Sign Up',
      stepKey: 'sign_up',
      onContinue: async () => {
        if (userCompletedSignUp) {
          return true;
        }

        try {
          await signUp(onboardingState.email ?? '', onboardingState.password ?? '');
          ReactPixel.trackCustom('User Signed Up');
          updateUserSetting({ appOnboardingState: 'finished' });
          return true;
        } catch (error: DoNotFixMeIAmAny) {
          if (error.code === 'auth/wrong-password' || error.code === 'auth/email-already-in-use') {
            setOnboardingState({
              ...onboardingState,
              emailError: 'An account already exists with this email.',
            });
          } else {
            setOnboardingState({
              ...onboardingState,
              emailError: 'Cannot log in. Please contact support.',
            });
          }
        }

        return false;
      },
      canContinue:
        userCompletedSignUp ||
        (onboardingState.email !== undefined &&
          onboardingState.password !== undefined &&
          onboardingState.password.length >= 8 &&
          EmailSchema.safeParse(onboardingState.email).success),
      component: () => (
        <Signup
          setEmail={(email: string) => {
            setOnboardingState({ ...onboardingState, email });
          }}
          setPassword={(password: string) => {
            setOnboardingState({ ...onboardingState, password });
          }}
          onboardingState={onboardingState}
        />
      ),
    },
    {
      continueText: onboardingState.trialStarted ? 'Continue' : 'Get Started',
      stepKey: 'trial',
      onContinue: async () => {
        if (onboardingState.trialStarted) {
          return true;
        }

        ReactPixel.trackCustom('Trial Start Clicked');
        startPaddleCheckout();
        return false;
      },
      canContinue: true,
      component: () => <PaddleCheckout />,
    },
  ];

  return (
    <OnboardingFlow
      onboardingState={onboardingState}
      setOnboardingState={setOnboardingState}
      onboardingSteps={steps}
      analyticsKey="receipt_scanner"
    />
  );
};
